import { useEffect, useState } from 'react'
import { Settings as SettingsIcon, Loader2, Moon, Sun, CheckCircle2, XCircle, Save, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { api, getApiErrorMessage } from '@/lib/api'
import { useTheme } from '@/context/ThemeContext'
import { getAdminRole, canAccess } from '@/lib/auth'
import { toast } from 'sonner'

interface AdminSettings {
  app_env: string
  debug: boolean
  log_level: string
  api_version: string
  invite_only_registration: boolean
  rate_limit_enabled: boolean
  mx_hostname: string
  autodiscover_hostname: string
  webmail_hostname: string
  default_from_email: string
  storage: {
    type: string
    bucket: string
    region: string
    endpoint: string
  }
  integrations: {
    sentry_configured: boolean
    waitlist_worker_configured: boolean
    dodo_payment_configured: boolean
    modoboa_api_url: string
  }
  email: {
    smtp_host: string
    smtp_port: number
    smtp_tx_host: string
    smtp_tx_port: number
  }
  jwt: {
    access_token_expiry_seconds: number
    refresh_token_expiry_seconds: number
    algorithm: string
  }
  editable_settings: Record<string, string>
}

const SettingRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between py-2 border-b last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium">{value}</span>
  </div>
)

export function Settings() {
  const { theme, toggleTheme } = useTheme()
  const [settings, setSettings] = useState<AdminSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const role = getAdminRole()
  const isFounder = canAccess(role, 'founder')

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/admin/settings')
        setSettings(res.data)
      } catch {
        toast.error('Failed to load settings')
      } finally {
        setLoading(false)
      }
    }
    fetchSettings()
  }, [])

  const editableSettings = settings?.editable_settings || {}

  const updateEditable = (key: string, value: string) => {
    setSettings((prev) =>
      prev ? { ...prev, editable_settings: { ...prev.editable_settings, [key]: value } } : prev
    )
  }

  const removeEditable = (key: string) => {
    setSettings((prev) => {
      if (!prev) return prev
      const next = { ...prev.editable_settings }
      delete next[key]
      return { ...prev, editable_settings: next }
    })
  }

  const addNewSetting = () => {
    const key = newKey.trim()
    const value = newValue
    if (!key) {
      toast.error('Setting key is required')
      return
    }
    if (key in editableSettings) {
      toast.error('Key already exists')
      return
    }
    updateEditable(key, value)
    setNewKey('')
    setNewValue('')
  }

  const saveSettings = async () => {
    if (!settings) return
    setSaving(true)
    try {
      await api.put('/admin/platform-settings', { settings: editableSettings })
      toast.success('Platform settings saved')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save settings'))
    } finally {
      setSaving(false)
    }
  }

  const booleanBadge = (value: boolean) =>
    value ? (
      <Badge variant="default" className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/10">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Enabled
      </Badge>
    ) : (
      <Badge variant="secondary" className="text-muted-foreground">
        <XCircle className="h-3 w-3 mr-1" />
        Disabled
      </Badge>
    )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-5 w-5 text-primary" />
          Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Platform configuration and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Appearance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                <span className="text-sm font-medium">Theme</span>
              </div>
              <Button variant="outline" size="sm" onClick={toggleTheme}>
                {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Environment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="App Env" value={settings?.app_env} />
            <SettingRow label="API Version" value={settings?.api_version} />
            <SettingRow label="Log Level" value={settings?.log_level} />
            <SettingRow label="Debug Mode" value={booleanBadge(settings?.debug ?? false)} />
            <SettingRow label="Rate Limiting" value={booleanBadge(settings?.rate_limit_enabled ?? false)} />
            <SettingRow label="Invite-Only Registration" value={booleanBadge(settings?.invite_only_registration ?? false)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Mail Hostnames</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="MX Hostname" value={settings?.mx_hostname} />
            <SettingRow label="Autodiscover" value={settings?.autodiscover_hostname} />
            <SettingRow label="Webmail" value={settings?.webmail_hostname} />
            <SettingRow label="Default From" value={settings?.default_from_email} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Storage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="Type" value={settings?.storage.type} />
            <SettingRow label="Bucket" value={settings?.storage.bucket} />
            <SettingRow label="Region" value={settings?.storage.region} />
            <SettingRow label="Endpoint" value={settings?.storage.endpoint} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Integrations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="Sentry" value={booleanBadge(settings?.integrations.sentry_configured ?? false)} />
            <SettingRow label="Waitlist Worker" value={booleanBadge(settings?.integrations.waitlist_worker_configured ?? false)} />
            <SettingRow label="Dodo Payments" value={booleanBadge(settings?.integrations.dodo_payment_configured ?? false)} />
            <SettingRow label="Modoboa API" value={settings?.integrations.modoboa_api_url} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Email Routing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="SMTP Host" value={settings?.email.smtp_host} />
            <SettingRow label="SMTP Port" value={settings?.email.smtp_port} />
            <SettingRow label="Transactional SMTP Host" value={settings?.email.smtp_tx_host} />
            <SettingRow label="Transactional SMTP Port" value={settings?.email.smtp_tx_port} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">JWT</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <SettingRow label="Algorithm" value={settings?.jwt.algorithm} />
            <SettingRow label="Access Token Expiry" value={`${settings?.jwt.access_token_expiry_seconds}s`} />
            <SettingRow label="Refresh Token Expiry" value={`${settings?.jwt.refresh_token_expiry_seconds}s`} />
          </CardContent>
        </Card>

        {isFounder && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm flex items-center justify-between">
                <span>Editable Platform Settings</span>
                <Button size="sm" onClick={saveSettings} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(editableSettings).length === 0 && (
                <p className="text-sm text-muted-foreground">No custom settings defined yet.</p>
              )}
              {Object.entries(editableSettings).map(([key, value]) => (
                <div key={key} className="flex items-start gap-2">
                  <Input value={key} disabled className="w-1/3 bg-muted" />
                  <Input
                    value={value}
                    onChange={(e) => updateEditable(key, e.target.value)}
                    placeholder="Value"
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeEditable(key)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex items-start gap-2 pt-2 border-t">
                <Input
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="new_key"
                  className="w-1/3"
                />
                <Input
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="value"
                  className="flex-1"
                />
                <Button variant="outline" size="icon" onClick={addNewSetting}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
